import { FileOutlined } from '@ant-design/icons';
import { Button, ConfigProvider, Typography, Upload, UploadProps } from 'antd';
import { DraggerProps } from 'antd/es/upload';
import clsx from 'clsx';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { TrashIcon } from '../../icons/trash-icon';
import { UploadIcon } from '../../icons/upload-icon';
import IconWrapper from '../icon-wrapper/icon-wrapper';
import styles from './file-upload.module.css';

interface Props {
  fileUploadProps: DraggerProps;
  fileUploadDescription?: string;
}

const FileUpload = ({ fileUploadProps, fileUploadDescription }: Props): JSX.Element => {
  const { t } = useTranslation('common');
  const renderFileItem: UploadProps['itemRender'] = (originNode, file, fileList, actions) => {
    const size = file.size ? `${(file.size / 1024).toFixed(2)} KB` : file.size === 0 ? '0 KB' : '';
    const originProps = originNode.props as { className?: string; children?: React.ReactNode } | undefined;

    const child = originProps?.children;
    const childClassName = React.isValidElement(child) ? (child.props as { className?: string }).className : undefined;

    return (
      <div className={originProps?.className ?? childClassName}>
        {file.thumbUrl ? (
          <a className="ant-upload-list-item-thumbnail" href={file.url || file.thumbUrl}>
            <img src={file.thumbUrl} alt={file.name} className="ant-upload-list-item-image" />
          </a>
        ) : (
          <div className="ant-upload-list-item-thumbnail ant-upload-list-item-file">
            <FileOutlined />
          </div>
        )}
        <div className={clsx(styles.uploadListItemName, 'ant-upload-list-item-name')}>
          <span>{file.name}</span>
          <span className={styles.uploadListItemSize}>{size}</span>
        </div>
        <Button
          onClick={() => actions.remove()}
          type="text"
          shape="circle"
          icon={
            <IconWrapper>
              <TrashIcon />
            </IconWrapper>
          }
        />
      </div>
    );
  };

  return (
    <ConfigProvider
      theme={{
        token: {
          fontWeightStrong: 400,
        },
      }}
    >
      <Upload.Dragger
        {...fileUploadProps}
        listType="picture"
        className={styles.uploadComponent}
        itemRender={renderFileItem}
      >
        <div>
          <Typography.Text type="success">
            <UploadIcon className={styles.uploadIcon} />
          </Typography.Text>
        </div>

        <Typography.Title level={5} className={styles.title} data-testid="fileUploadTitleId">
          {t('fileUpload.uploadTitle')}
        </Typography.Title>
        <Typography.Text data-testid="fileUploadDescriptionId" className={styles.subtitle}>
          {fileUploadDescription || t('fileUpload.uploadDescription')}
        </Typography.Text>
      </Upload.Dragger>
    </ConfigProvider>
  );
};

export default FileUpload;
